import type {
  ColumnType,
  DatasetManifestEntry,
  GroupBy,
  Measure,
  ReportDefinition,
  ReportFilter,
} from '../types/reporting';
import {
  NO_RELATED,
  referencedRelatedKeys,
  resolveColumnMeta,
  splitRef,
  type RelatedManifests,
} from './columnRefs';
import { MAX_CATEGORIES, MAX_DATE_CATEGORIES } from './chartFormat';
import {
  type AxisConstraint,
  boundsOf,
  tighterLower,
  tighterUpper,
} from './filterBounds';
import { isMoneyDatasetColumn } from './formatValue';
import { pacificTodayISO, resolveRelativeDateRange } from './reportingDates';

export const DETAIL_ROW_LIMIT = 10000;
export const PIVOT_MAX_VALUES = 30;

// Scalar column carrying the TRUE number of chart categories before the SQL
// top-N trim, so the UI can disclose "showing top 50 of N". Stripped from the
// result before it reaches ResultsChart (splitDetailChartResult in
// detailChart.ts) — ResultsChart treats every non-dimension column as a series,
// so leaving it in would draw the total as a bar.
export const CHART_CATEGORY_TOTAL_COLUMN = '__chart_category_total';

/**
 * INTERNAL, NEVER-PERSISTED marker set by `detailChartDefinition` (detailChart.ts)
 * on the synthetic grouped definition it derives for a detail report's Chart tab.
 *
 * It exists so the top-N chart contract (measure-DESC ordering + LIMIT
 * MAX_CATEGORIES + the category-total scalar) can NEVER be reached by a
 * user-persisted definition: the marker is added by one function, is not part of
 * `ReportDefinition`, and is never sent to the API. A plain grouped definition —
 * however it was authored — compiles without a LIMIT, exactly as before.
 */
export interface DetailChartCompileDefinition extends ReportDefinition {
  __detail_chart?: true;
}

const isDetailChartCompile = (def: ReportDefinition): boolean =>
  (def as DetailChartCompileDefinition).__detail_chart === true;

export class ReportCompileError extends Error {}

const q = (ident: string): string => `"${ident.replace(/"/g, '""')}"`;

const lit = (value: unknown): string => {
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return `'${String(value).replace(/'/g, "''")}'`;
};

// Renders a column ref as SQL. Non-joined queries use plain quoting (byte-
// identical output to the pre-join compiler for materialized columns); joined
// queries qualify every ref with its table, because primary and related tables
// can share column names (visits.status vs episodes.status) and unqualified refs
// would be ambiguous. A DERIVED column (manifest metadata carries derived_from)
// is not in the fetched Parquet — it is computed here from its source column
// against the current Pacific business day, so every consumer (bucket, filter,
// detail, order) that routes a ref through this resolver gets the derivation.
type ColResolver = (ref: string) => string;

// Whether a ref resolves to a DERIVED (serve-time computed) column.
function isDerivedRef(
  ref: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): boolean {
  return !!resolveColumnMeta(ref, dataset, related)?.derived_from;
}

function makeColResolver(
  def: ReportDefinition,
  joined: boolean,
  dataset: DatasetManifestEntry,
  related: RelatedManifests,
  today: string
): ColResolver {
  const base = joined
    ? (ref: string): string => {
        const { dataset: dsKey, column } = splitRef(ref);
        return `${q(dsKey ?? def.dataset_key)}.${q(column)}`;
      }
    : q;
  return (ref: string): string => {
    const meta = resolveColumnMeta(ref, dataset, related);
    const derived = meta?.derived_from;
    if (derived && derived.kind === 'days_since') {
      // date_diff('day', start, end) = end - start (in whole days), so the
      // integer day count from the source DATE column to the Pacific business
      // "today" reproduces the old build-time (today - submitted_date) exactly,
      // recomputed per run. A NULL source yields NULL, matching (today - NULL).
      const { dataset: dsKey } = splitRef(ref);
      const sourceRef =
        dsKey === null ? derived.column : `${dsKey}.${derived.column}`;
      return `date_diff('day', ${base(sourceRef)}, DATE ${lit(today)})`;
    }
    return base(ref);
  };
}

function knownRef(
  ref: string,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): boolean {
  const { dataset: dsKey, column } = splitRef(ref);
  const entry = dsKey === null ? dataset : related.get(dsKey);
  return entry?.columns.some(c => c.name === column) ?? false;
}

// One LEFT JOIN per referenced related dataset, along the declared many-to-one
// edge. Declared+grain-enforced target uniqueness (backend unique_keys
// invariant) is what makes this fan-out-free: output rows ≤ primary rows.
function fromClause(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  relatedKeys: string[]
): string {
  const table = q(def.dataset_key);
  const joins = relatedKeys.map(key => {
    const rel = (dataset.relationships ?? []).find(r => r.target === key);
    if (!rel || rel.cardinality !== 'many_to_one') {
      throw new ReportCompileError(
        `Columns from "${key}" can't be combined with ${dataset.label}.`
      );
    }
    return ` LEFT JOIN ${q(key)} ON ${q(def.dataset_key)}.${q(rel.local_key)} = ${q(key)}.${q(rel.target_key)}`;
  });
  return `${table}${joins.join('')}`;
}

// The related datasets a query LEFT JOINs: every EXPLICITLY joined dataset
// (def.joined_datasets — the user added it like a filter, so it joins even with
// zero of its columns selected) in the user's join order, followed by any dataset
// a surviving column ref names that isn't already listed (sorted, for a
// deterministic tail). The union with referenced datasets is a correctness guard
// for a definition that reaches the compiler WITHOUT having been backfilled — a
// direct test caller, or a legacy per-column definition — where a dotted ref must
// still have its table in FROM or the qualified SQL is invalid. For a backfilled
// definition joined_datasets already ⊇ the referenced datasets, so the tail is
// empty and the result is exactly joined_datasets. A primary-only definition
// (no joins, no dotted refs) yields [], so compileReport stays byte-identical to
// the pre-join output.
export function joinedDatasetKeys(def: ReportDefinition): string[] {
  const joined = def.joined_datasets ?? [];
  const extra = [...referencedRelatedKeys(def)]
    .filter(key => !joined.includes(key))
    .sort();
  return [...joined, ...extra];
}

export function bucketLabels(edges: number[]): string[] {
  const labels: string[] = [];
  let lower = 0;
  for (const edge of edges) {
    labels.push(`${lower}-${edge - 1}`);
    lower = edge;
  }
  labels.push(`${lower}+`);
  return labels;
}

function bucketExpr(ref: string, edges: number[], col: ColResolver): string {
  const labels = bucketLabels(edges);
  const cases = edges
    .map((edge, i) => `WHEN ${col(ref)} < ${edge} THEN ${lit(labels[i])}`)
    .join(' ');
  return `CASE WHEN ${col(ref)} IS NULL THEN NULL ${cases} ELSE ${lit(labels[labels.length - 1])} END`;
}

function dimExpr(groupBy: GroupBy, col: ColResolver): string {
  if (!groupBy.transform) return col(groupBy.column);
  if (groupBy.transform.type === 'bucket') {
    return bucketExpr(groupBy.column, groupBy.transform.edges, col);
  }
  return `CAST(date_trunc('${groupBy.transform.unit}', ${col(groupBy.column)}) AS DATE)`;
}

export function dimAlias(groupBy: GroupBy): string {
  return groupBy.column;
}

export function measureAlias(measure: Measure): string {
  return measure.agg === 'count' ? 'count' : `${measure.agg}_${measure.column}`;
}

function measureExpr(measure: Measure, col: ColResolver): string {
  if (measure.agg === 'count') return 'count(*)';
  if (!measure.column) {
    throw new ReportCompileError(
      `The ${measure.agg} measure needs a column to aggregate.`
    );
  }
  // Accumulate money aggregates in DECIMAL(18,2) instead of float64 so cents
  // stay exact — sum(CAST(col AS DECIMAL)) never drifts the way summing binary
  // floats (0.1 + 0.2 → 0.30000000000000004) does. Casting the column before
  // the aggregate keeps the whole accumulation in decimal space. Applies in the
  // pivot USING clause too (it shares measureExpr), so pivoted money value
  // columns are exact as well.
  // measure.column is always a raw dataset column, so use EXACT money membership
  // — not the broader result-key heuristic. A unitless numeric metric that merely
  // ends in a money word (e.g. "workload_balance") must not be rounded through
  // DECIMAL(18,2); only a real money column (balance/total_charges/paid_amount)
  // is accumulated in decimal space.
  const source = isMoneyDatasetColumn(measure.column)
    ? `CAST(${col(measure.column)} AS DECIMAL(18,2))`
    : col(measure.column);
  return `${measure.agg}(${source})`;
}

// Neutralizes LIKE/ILIKE wildcards in a value the user meant literally. The
// backslash must be escaped FIRST, or escaping `%` afterwards would re-escape
// the backslash this function just inserted. Paired with `ESCAPE '\'` at the
// call site — DuckDB's single-quoted strings are standard-conforming (no
// backslash processing), so `'\'` is one literal backslash.
const escapeLikeLiteral = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/[%_]/g, ch => `\\${ch}`);

function filterSql(
  filter: ReportFilter,
  type: ColumnType,
  col: ColResolver
): string {
  const colSql = col(filter.column);
  switch (filter.op) {
    case 'eq':
      return `${colSql} = ${lit(filter.value)}`;
    case 'neq':
      return `${colSql} IS DISTINCT FROM ${lit(filter.value)}`;
    case 'in':
      // Drop any member that isn't castable for the column type. An `in` list
      // is active as long as one member is valid (see isFilterActive), so a
      // blank/invalid member (e.g. `['2026-05-01', '']` on a date column) must
      // not reach DuckDB as `IN ('2026-05-01', '')` and fail to cast — emit only
      // the valid members.
      return `${colSql} IN (${(filter.value as unknown[])
        .filter(v => isValidBound(v, type))
        .map(lit)
        .join(', ')})`;
    case 'contains':
      // The user typed literal text, not a pattern. ILIKE reads `%` and `_` as
      // wildcards, so an unescaped "100%" would match "100" followed by
      // anything and "MRN_1" would match "MRN" + any single char + "1" — the
      // report silently returning MORE rows than the filter shown on screen
      // describes. Escape the two wildcards (and the escape character itself,
      // so a literal backslash stays literal) and declare the escape char
      // explicitly, since DuckDB's default is no escape at all.
      return `${colSql} ILIKE ${lit(
        `%${escapeLikeLiteral(String(filter.value))}%`
      )} ESCAPE '\\'`;
    case 'gt':
      return `${colSql} > ${lit(filter.value)}`;
    case 'gte':
      return `${colSql} >= ${lit(filter.value)}`;
    case 'lt':
      return `${colSql} < ${lit(filter.value)}`;
    case 'lte':
      return `${colSql} <= ${lit(filter.value)}`;
    case 'between': {
      const [lo, hi] = filter.value as [unknown, unknown];
      return `${colSql} BETWEEN ${lit(lo)} AND ${lit(hi)}`;
    }
    case 'is_null':
      return `${colSql} IS NULL`;
    case 'not_null':
      return `${colSql} IS NOT NULL`;
    case 'relative_date':
      throw new ReportCompileError(
        'relative_date filters must be resolved with prepareDefinition before compiling'
      );
  }
}

const isBlankValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value);
  return false;
};

// A regex match alone would accept impossible calendar dates like 2026-02-30,
// which DuckDB rejects when casting VARCHAR → DATE. Parse the components and
// confirm the day is in range for the month (using the numeric Date
// constructor, which does not string-parse: day 0 of month+1 is the last valid
// day of `month`).
const isValidIsoDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return false;
  const lastDayOfMonth = new Date(year, month, 0).getDate();
  return day >= 1 && day <= lastDayOfMonth;
};

// A bound is usable only if DuckDB can compare it against the column without a
// cast error, so validation is column-type aware. A date column needs a real
// ISO calendar date — a bare number or a date-shaped-but-impossible string is
// rejected. A number column needs a finite number — a date-shaped string is
// rejected. Any other column type just needs a non-blank value. This keeps
// invalid or mismatched bounds (from mid-edit UI or an externally-supplied
// definition) from reaching DuckDB as `BETWEEN 'a' AND 'b'` and breaking the
// report with a conversion/binder error.
const isValidBound = (value: unknown, type: ColumnType): boolean => {
  if (type === 'number')
    return typeof value === 'number' && Number.isFinite(value);
  if (type === 'date')
    return typeof value === 'string' && isValidIsoDate(value);
  return !isBlankValue(value);
};

const columnTypeOf = (
  dataset: DatasetManifestEntry,
  related: RelatedManifests,
  ref: string
): ColumnType => {
  const { dataset: dsKey, column } = splitRef(ref);
  const entry = dsKey === null ? dataset : related.get(dsKey);
  return entry?.columns.find(c => c.name === column)?.type ?? 'string';
};

// A filter is only applied once its value is complete and castable for the
// column's type. An incomplete or type-mismatched filter (a between with an
// empty/invalid/mismatched bound, an empty scalar value) is treated as inactive
// and skipped, so mid-edit or externally-supplied definitions never emit broken
// SQL like `BETWEEN '2026-05-01' AND ''` and break the report.
export function isFilterActive(
  filter: ReportFilter,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): boolean {
  const type = columnTypeOf(dataset, related, filter.column);
  switch (filter.op) {
    case 'is_null':
    case 'not_null':
    case 'relative_date':
      return true;
    case 'between': {
      const bounds = filter.value;
      if (!Array.isArray(bounds) || bounds.length < 2) return false;
      return isValidBound(bounds[0], type) && isValidBound(bounds[1], type);
    }
    case 'in':
      // Active once at least one member is castable for the column type — the
      // same predicate filterSql uses to drop members, so the two never
      // disagree (an active `in` always emits a non-empty, all-valid list).
      return (
        Array.isArray(filter.value) &&
        filter.value.some(v => isValidBound(v, type))
      );
    case 'contains':
      // A text search is inherently a string op; only blankness matters.
      return !isBlankValue(filter.value);
    default:
      return isValidBound(filter.value, type);
  }
}

function whereClause(
  filters: ReportFilter[],
  dataset: DatasetManifestEntry,
  related: RelatedManifests,
  col: ColResolver
): string {
  const active = filters.filter(f => isFilterActive(f, dataset, related));
  if (active.length === 0) return '';
  return ` WHERE ${active
    .map(f => filterSql(f, columnTypeOf(dataset, related, f.column), col))
    .join(' AND ')}`;
}

function orderClause(
  sort: ReportDefinition['sort'],
  validAliases: string[]
): string {
  const applicable = sort.filter(s => validAliases.includes(s.by));
  if (applicable.length === 0) return '';
  const parts = applicable.map(
    s => `${q(s.by)} ${s.dir === 'desc' ? 'DESC' : 'ASC'}`
  );
  return ` ORDER BY ${parts.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Gap-fill date spine (design §1-D3, §3)
// ---------------------------------------------------------------------------

// UTC-safe ISO date arithmetic. The bounds prepareDefinition produces are
// Pacific-derived ISO STRINGS; parsing them through the local-time Date
// constructor would shift the day west of UTC, so every shift goes through
// Date.UTC — the same pattern reportingDates.ts uses.
const isoParts = (iso: string): [number, number, number] => {
  const [y, m, d] = iso.split('-').map(Number);
  return [y, m, d];
};

const isoUtcMs = (iso: string): number => {
  const [y, m, d] = isoParts(iso);
  return Date.UTC(y, m - 1, d);
};

const shiftIsoDays = (iso: string, days: number): string => {
  const shifted = new Date(isoUtcMs(iso));
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
};

const DAY_MS = 86400000;

// Inclusive day span: a start === end window spans 1 day.
const inclusiveDaySpan = (start: string, end: string): number =>
  (isoUtcMs(end) - isoUtcMs(start)) / DAY_MS + 1;

// Ops whose bounds `boundsOf` can represent. ANY other active operator on the
// grouped column (neq, in, contains, is_null, not_null, …) disables gap-fill
// outright rather than silently over-filling: `between Jan 1-10 AND neq Jan 5`
// must not re-add Jan 5 as a synthetic $0 row the user's own filter excludes.
// Failing closed here keeps the report byte-identical to today's behaviour and
// keeps the trigger auditable, instead of re-applying discrete predicates to
// spine rows.
const GAP_FILL_BOUND_OPS: ReadonlySet<string> = new Set([
  'between',
  'eq',
  'gte',
  'lte',
  'gt',
  'lt',
]);

interface SpineWindow {
  start: string;
  end: string;
}

/**
 * The concrete, bounded date window a zero-filled spine should cover, or null
 * when ANY trigger condition fails (in which case gap-fill is silently inert and
 * the compiled SQL is byte-identical to a definition with no `gap_fill`).
 *
 * MUST be called on the PREPARED definition — `relative_date` has to have been
 * resolved into a concrete `between` before there is a window to read.
 *
 * Deliberately does its OWN column selection (design §3): unlike
 * `deriveExtractScope` it applies no prunability and no derivation test, and it
 * accepts dotted refs, because "which days exist in this result" is a question
 * about the query the browser is about to run, not about what the server can
 * prune. Sharing the whole function would make `gap_fill: true` silently no-op
 * on legitimate columns; sharing only the bound primitives keeps both honest.
 */
function gapFillWindow(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): SpineWindow | null {
  // (a) opt-in only.
  if (def.gap_fill !== true) return null;
  // The synthetic detail-chart definition is a presentation-derived rollup with
  // no date axis; `detailChartDefinition` already strips `gap_fill`, and this is
  // the second, independent defense.
  if (isDetailChartCompile(def)) return null;
  // (b) exactly one raw (untransformed) date group-by, no pivot.
  if (def.group_bys.length !== 1 || def.pivot_column) return null;
  const groupBy = def.group_bys[0];
  if (groupBy.transform) return null;
  if (columnTypeOf(dataset, related, groupBy.column) !== 'date') return null;
  // (c) at least one measure, and EVERY measure additive. The "at least one"
  // clause matters: without it the `every` would be vacuously true for a
  // measure-less definition (which never reaches the grouped branch anyway, but
  // the trigger must not depend on that).
  if (def.measures.length === 0) return null;
  if (!def.measures.every(m => m.agg === 'sum' || m.agg === 'count'))
    return null;
  // (d) + (e): intersect every ACTIVE filter on the grouped column, refusing on
  // any operator whose bounds we cannot represent.
  const constraint: AxisConstraint = {};
  for (const filter of def.filters) {
    if (filter.column !== groupBy.column) continue;
    if (!isFilterActive(filter, dataset, related)) continue;
    if (!GAP_FILL_BOUND_OPS.has(filter.op)) return null;
    const bounds = boundsOf(filter);
    if (!bounds) return null;
    if (bounds.lower)
      constraint.lower = tighterLower(constraint.lower, bounds.lower);
    if (bounds.upper)
      constraint.upper = tighterUpper(constraint.upper, bounds.upper);
  }
  const { lower, upper } = constraint;
  if (!lower || !upper) return null;
  // Exclusive endpoints move inward by a day so the spine covers exactly the
  // days the WHERE clause admits.
  const start = lower.inclusive ? lower.value : shiftIsoDays(lower.value, 1);
  const end = upper.inclusive ? upper.value : shiftIsoDays(upper.value, -1);
  // Empty or reversed window (including equal exclusive bounds) -> no spine.
  if (start > end) return null;
  // (f) The cap is the CHART's own date-axis ceiling, imported rather than
  // restated, so the two can never drift. Past it, capCategories would refuse to
  // render the chart at all — filling a window wider than the chart can draw
  // would turn a working sparse chart into a dead one. Above the cap the spine
  // simply does not emit: the table stays sparse and the chart renders exactly
  // as it does today, so there is no reachable state where gap-fill kills a
  // chart that renders now.
  //
  // The cap stays a CALENDAR-day measure even though the spine only zero-fills
  // weekdays: the union arm can restore categories up to the full calendar span
  // (every date the aggregate actually produced is admitted), so re-deriving
  // this as "<= 92 weekdays" would silently widen eligible windows to ~128
  // calendar days. A 92-calendar-day window now typically renders ~66 bars.
  if (inclusiveDaySpan(start, end) > MAX_DATE_CATEGORIES) return null;
  return { start, end };
}

/**
 * Whether `def.chart` is a usable authored chart config.
 *
 * Shared by `validateDefinition` (which DROPS a failing chart) and
 * `validChartConfig` in detailChart.ts (which gates the Chart tab), so the tab
 * and the stored definition can never disagree about what "valid" means.
 *
 * Membership in `def.columns` mirrors the backend's semantic check: the chart
 * may only address columns the report actually selected. A detail report with an
 * EMPTY `columns` list means "all columns" at the SQL level, but the backend
 * check is literal membership, so this is literal too — the two stacks agree.
 */
export function chartConfigValid(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): boolean {
  const chart = def.chart;
  if (!chart) return false;
  // A detail-report chart has no pivot at chart grain: pivot_column && measures
  // wins branch selection ahead of the group_bys/__detail_chart branch in
  // compileReport, so a stale pivot_column would silently skip the top-N/
  // category-total contract. detailChartDefinition neutralizes this by setting
  // pivot_column: null on the derived definition; this is the belt-and-braces
  // half so a definition that reaches here with a lingering pivot_column can
  // never be treated as a valid chart config in the first place.
  if (def.pivot_column) return false;
  if (chart.agg !== 'sum' && chart.agg !== 'count') return false;
  const selected = new Set(def.columns);
  if (!selected.has(chart.group_by)) return false;
  if (!knownRef(chart.group_by, dataset, related)) return false;
  // A categorical dimension: a raw numeric column is a measure, not an axis.
  if (columnTypeOf(dataset, related, chart.group_by) === 'number') return false;
  if (chart.agg === 'count') {
    // count aggregates rows, so a column would be meaningless — and accepting
    // one would let two configs that mean the same thing disagree on shape.
    return chart.column === undefined || chart.column === null;
  }
  if (!chart.column) return false;
  if (!selected.has(chart.column)) return false;
  if (!knownRef(chart.column, dataset, related)) return false;
  return columnTypeOf(dataset, related, chart.column) === 'number';
}

function referencedColumns(def: ReportDefinition): string[] {
  const refs = [...def.columns];
  refs.push(...def.group_bys.map(g => g.column));
  refs.push(
    ...def.measures
      .filter(m => m.column !== undefined)
      .map(m => m.column as string)
  );
  if (def.pivot_column) refs.push(def.pivot_column.column);
  refs.push(...def.filters.map(f => f.column));
  // sort.by is intentionally NOT asserted here: a sort target may legitimately
  // be a measure alias ("count", "sum_charge") rather than a raw dataset column,
  // and orderClause already drops any sort whose target isn't a valid ORDER BY
  // alias for the current query. Asserting it as a column would wrongly reject a
  // grouped view sorted by one of its measures.
  return refs;
}

function assertColumnsExist(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): void {
  const unknown = referencedColumns(def).filter(
    ref => !knownRef(ref, dataset, related)
  );
  if (unknown.length > 0) {
    throw new ReportCompileError(`Unknown columns: ${unknown.join(', ')}`);
  }
}

function numericColumnNames(dataset: DatasetManifestEntry): Set<string> {
  return new Set(
    dataset.columns.filter(c => c.type === 'number').map(c => c.name)
  );
}

// Every non-count measure must aggregate a numeric column. This runs up front,
// independent of the query branch (grouped / pivot / detail), so an invalid
// measure can never slip through — e.g. sum("status") reaching DuckDB as a
// binder error, or a stray aggregate on a detail query being silently ignored.
function assertMeasuresValid(
  def: ReportDefinition,
  dataset: DatasetManifestEntry
): void {
  const numeric = numericColumnNames(dataset);
  // A non-count aggregate only produces a column in the grouped or pivot branch
  // (both need a GROUP BY / PIVOT). Without either, compileReport falls through
  // to the detail branch, which never emits the aggregate — the measure would be
  // silently dropped and the report would answer a different question than the
  // definition on screen. Reject it here so validation is branch-independent.
  // pivot_column is optional (`GroupBy | null`) — a persisted/API-valid
  // definition may OMIT it rather than set it to null, so `!== null` is true for
  // `undefined` and would wave a measure through with no grouping. Coerce to
  // boolean so an omitted pivot is treated the same as a null one.
  const hasGrouping = def.group_bys.length > 0 || !!def.pivot_column;
  for (const measure of def.measures) {
    if (measure.agg === 'count') continue;
    if (
      measure.column !== undefined &&
      splitRef(measure.column).dataset !== null
    ) {
      // v1: measures aggregate primary-dataset columns only (LEFT-JOIN NULL
      // semantics for aggregates are deliberately not surfaced yet).
      throw new ReportCompileError(
        `Values can only use ${dataset.label} columns.`
      );
    }
    if (!measure.column) {
      throw new ReportCompileError(
        `The ${measure.agg} measure needs a column to aggregate.`
      );
    }
    if (!numeric.has(measure.column)) {
      throw new ReportCompileError(
        `The ${measure.agg} measure needs a numeric column; "${measure.column}" is not numeric.`
      );
    }
    if (!hasGrouping) {
      throw new ReportCompileError(
        `The ${measure.agg} measure needs a group-by or pivot to aggregate over.`
      );
    }
  }
}

// The directional window of a relative_date filter. The value is exactly one of
// { last_days } (backward) XOR { next_days } (forward). Any other shape — both
// keys, neither key, a non-numeric or non-positive count — comes only from an
// externally-supplied/weakly-validated definition and can't resolve to a real
// range (resolveRelativeDateRange would throw or produce Invalid time value), so
// it returns null and the filter drops out as inactive.
const relativeDateWindow = (
  value: unknown
): { days: number; direction: 'last' | 'next' } | null => {
  if (value === null || typeof value !== 'object') return null;
  const hasNext = 'next_days' in value;
  const hasLast = 'last_days' in value;
  // Exactly one key: both-set and neither-set are malformed and drop.
  if (hasNext === hasLast) return null;
  const direction = hasNext ? 'next' : 'last';
  const days = (value as Record<string, unknown>)[
    hasNext ? 'next_days' : 'last_days'
  ];
  if (typeof days !== 'number' || !Number.isFinite(days) || days < 1)
    return null;
  return { days, direction };
};

export function prepareDefinition(def: ReportDefinition): ReportDefinition {
  return {
    ...def,
    // flatMap so a malformed relative_date drops out entirely and acts as an
    // inactive filter, rather than throwing while resolving its window.
    filters: def.filters.flatMap(filter => {
      if (filter.op !== 'relative_date') return [filter];
      const window = relativeDateWindow(filter.value);
      if (window === null) return [];
      const { start, end } = resolveRelativeDateRange(
        window.days,
        window.direction
      );
      return [
        {
          column: filter.column,
          op: 'between' as const,
          value: [start, end],
        },
      ];
    }),
  };
}

// Mirror of the backend's save-time _FILTER_OPS_BY_TYPE contract
// (app/api/reporting.py): the filter ops DuckDB can execute against each
// column type. validateDefinition uses it to DROP (with the "removed
// columns" notice) any stored filter whose op no longer fits its column's
// CURRENT type — a column retyped by a dataset version bump otherwise
// reaches DuckDB as a binder/conversion error (contains → ILIKE on a
// number; relative_date's resolved between on a string). Keep in sync with
// the backend table when adding ops.
const COMPATIBLE_FILTER_OPS: Record<ColumnType, ReadonlySet<string>> = {
  string: new Set(['eq', 'neq', 'in', 'contains', 'is_null', 'not_null']),
  number: new Set([
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'in',
    'is_null',
    'not_null',
  ]),
  date: new Set([
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'relative_date',
    'in',
    'is_null',
    'not_null',
  ]),
  boolean: new Set(['eq', 'neq', 'in', 'is_null', 'not_null']),
};

// Mirror of the backend's check_dimension: bucket buckets numbers,
// date_trunc truncates dates. Anything else means the column was retyped
// (drift) — drop the whole clause and record it, rather than emit SQL the
// binder rejects or that silently answers a different question.
function transformCompatible(groupBy: GroupBy, type: ColumnType): boolean {
  if (!groupBy.transform) return true;
  if (groupBy.transform.type === 'bucket') return type === 'number';
  return type === 'date'; // date_trunc
}

export function validateDefinition(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): { definition: ReportDefinition; dropped: string[] } {
  const dropped: string[] = [];
  const keep = (name: string): boolean => {
    if (knownRef(name, dataset, related)) return true;
    dropped.push(name);
    return false;
  };
  // Type-compat pass (round 4): a ref can survive the name check but carry a
  // clause its column's CURRENT type can't execute (the column was retyped by
  // a dataset version bump, or the registered extract predates a retype when
  // the runner's schema gate re-validates against extract types). Drop the
  // clause and record the ref — same notice channel as a removed column.
  const keepDimension = (g: GroupBy): boolean => {
    if (!keep(g.column)) return false;
    if (transformCompatible(g, columnTypeOf(dataset, related, g.column))) {
      return true;
    }
    dropped.push(g.column);
    return false;
  };
  const keepFilter = (f: ReportFilter): boolean => {
    if (!keep(f.column)) return false;
    const type = columnTypeOf(dataset, related, f.column);
    if (COMPATIBLE_FILTER_OPS[type].has(f.op)) return true;
    dropped.push(f.column);
    return false;
  };
  // Clean up measures from a loaded/externally-supplied report without silently
  // changing what the user asked for. A measure whose column vanished from the
  // dataset can't be re-selected, so it's dropped and its column recorded for
  // the "removed columns" notice. Any other invalid non-count measure (no column
  // chosen yet, or an aggregate on a non-numeric column) is kept as-is:
  // compileReport surfaces it as an error and FieldPanel prompts for a valid
  // numeric column, rather than downgrading a Sum to a Count that answers a
  // different question than the one on screen.
  const repairMeasure = (measure: Measure): Measure[] => {
    if (measure.agg === 'count') return [measure];
    if (
      measure.column !== undefined &&
      !knownRef(measure.column, dataset, related)
    ) {
      dropped.push(measure.column);
      return [];
    }
    return [measure];
  };
  // Collapse measures that resolve to the same output alias. Two measures with
  // the same agg+column (e.g. two Count measures, which both alias to "count")
  // are identical aggregations, so they'd compile to duplicate `... AS "count"`
  // SELECT items — DuckDB then returns two identically named result columns,
  // which surface downstream as duplicate React keys in the table/chart,
  // duplicate CSV headers, and an ambiguous sort target. Keeping the first
  // occurrence is lossless: the dropped duplicate produced the same column.
  const seenAliases = new Set<string>();
  const measures = def.measures.flatMap(repairMeasure).filter(measure => {
    const alias = measureAlias(measure);
    if (seenAliases.has(alias)) return false;
    seenAliases.add(alias);
    return true;
  });
  // Collapse group-bys that resolve to the same dimension alias. Two group-bys
  // on the same column both alias to that column (dimAlias), so they'd compile
  // to duplicate `... AS "<col>"` SELECT items — DuckDB then returns two
  // identically named result columns, surfacing as duplicate React table/chart
  // keys, duplicate CSV headers, and an ambiguous sort target. Source guards
  // (addGroupBy / setGroupByColumn) stop the live builder from ever creating one;
  // this is the net for loaded/externally-supplied/legacy definitions. Keeping
  // the first occurrence is lossless: the dropped duplicate produced the same
  // column, so it is collapsed silently rather than recorded as a removed column.
  const seenDims = new Set<string>();
  const groupBys = def.group_bys.filter(keepDimension).filter(g => {
    const alias = dimAlias(g);
    if (seenDims.has(alias)) return false;
    seenDims.add(alias);
    return true;
  });
  const measureAliases = new Set(measures.map(measureAlias));
  const columns = def.columns.filter(keep);
  const pivotColumn =
    def.pivot_column && keepDimension(def.pivot_column)
      ? def.pivot_column
      : null;
  const filters = def.filters.filter(keepFilter);
  // A sort target is valid only if the compiled query for THIS definition's mode
  // actually emits it as an addressable ORDER BY column — so mirror
  // compileReport's branch selection exactly. A pivot exposes only its
  // row-dimension aliases (value columns are generated dynamically and can't be
  // named); a grouped view exposes its dimension aliases plus measure aliases; a
  // detail view exposes its selected columns (empty = all). Gating on the raw
  // dataset column set alone would keep a detail-column sort (e.g. {by: 'balance'})
  // alive after switching to a grouped/pivot view: the query silently ignores it,
  // but saving persists it and it resurfaces when the view changes back.
  const dimAliases = groupBys.map(dimAlias);
  const emittedSortTargets =
    pivotColumn && measures.length > 0
      ? new Set(dimAliases)
      : groupBys.length > 0 && measures.length > 0
        ? new Set([...dimAliases, ...measureAliases])
        : new Set(
            columns.length > 0 ? columns : dataset.columns.map(c => c.name)
          );
  const keepSort = (by: string): boolean => {
    if (emittedSortTargets.has(by)) return true;
    // Record a target removed from the dataset entirely as a "removed column";
    // a target that's still a known column (or measure alias) but not emitted by
    // this view is stale sort state, cleared silently — the same way
    // setPivotColumn drops a measure/detail sort on entering a pivot.
    if (!knownRef(by, dataset, related) && !measureAliases.has(by))
      dropped.push(by);
    return false;
  };
  const definition: ReportDefinition = {
    ...def,
    columns,
    group_bys: groupBys,
    measures,
    pivot_column: pivotColumn,
    filters,
    sort: def.sort.filter(s => keepSort(s.by)),
  };
  // Authored detail-report chart config. A stale chart (its group_by/column no
  // longer among the SURVIVING selected columns, or retyped) is DROPPED rather
  // than surfaced as an error — the chart is presentation, not the report's
  // question, so losing it must never block opening the report. Deliberately not
  // recorded in `dropped`: that notice names removed COLUMNS, and the column that
  // vanished is already listed there by the `keep` pass above.
  const chartValid = chartConfigValid(definition, dataset, related);
  if (!chartValid) delete definition.chart;
  // Detail-report view normalization. A detail report's Chart view is valid iff
  // a valid `chart` block exists; anything else coerces to Table on open. This
  // closes a state reachable today (add a group-by, switch to Chart, remove the
  // group-by, save) where the tab strip would highlight Chart over a rendered
  // table. Scoped strictly to detail reports — a GROUPED definition's `view` is
  // never touched, so Expected Payments by Day's legitimate grouped
  // `view: 'bar'` survives verbatim.
  if (groupBys.length === 0 && !chartValid && definition.view === 'bar') {
    definition.view = 'table';
  }
  return { definition, dropped };
}

export function compileReport(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): string {
  assertColumnsExist(def, dataset, related);
  assertMeasuresValid(def, dataset);
  const relatedKeys = joinedDatasetKeys(def);
  const joined = relatedKeys.length > 0;
  // A single Pacific "today" per compile — the one clock source for every
  // DERIVED days_since column, matching resolveRelativeDateRange's pacificTodayISO.
  const today = pacificTodayISO();
  const col = makeColResolver(def, joined, dataset, related, today);
  const table = fromClause(def, dataset, relatedKeys);
  const where = whereClause(def.filters, dataset, related, col);

  if (def.pivot_column && def.measures.length > 0) {
    const dims = def.group_bys;
    const measureSourceColumns = [
      ...new Set(
        def.measures
          .filter(m => m.column !== undefined)
          .map(m => m.column as string)
      ),
    ];
    const innerParts = [
      ...dims.map(g => `${dimExpr(g, col)} AS ${q(dimAlias(g))}`),
      `${dimExpr(def.pivot_column, col)} AS ${q('__pivot')}`,
      // Joined mode (or a DERIVED source) must project the computed/qualified
      // expression AND alias it back to its bare name so the outer USING clause
      // (which addresses inner output columns by name) is identical in both modes.
      ...measureSourceColumns.map(ref =>
        joined || isDerivedRef(ref, dataset, related)
          ? `${col(ref)} AS ${q(ref)}`
          : q(ref)
      ),
    ];
    const inner = `SELECT ${innerParts.join(', ')} FROM ${table}${where}`;
    const using = def.measures
      .map(m => `${measureExpr(m, q)} AS ${q(measureAlias(m))}`)
      .join(', ');
    const groupBy = dims.length
      ? ` GROUP BY ${dims.map(g => q(dimAlias(g))).join(', ')}`
      : '';
    // Only the row-dimension aliases are addressable ORDER BY targets — the
    // pivoted value columns are generated dynamically and can't be named in
    // definition.sort. orderClause drops any sort on a non-dimension target, so
    // a header click on a pivot value column can never reach DuckDB; the sort
    // falls back to the default ascending dimension order.
    const dimAliases = dims.map(dimAlias);
    const order =
      orderClause(def.sort, dimAliases) ||
      (dims.length
        ? ` ORDER BY ${dimAliases.map(a => `${q(a)} ASC`).join(', ')}`
        : '');
    return `PIVOT (${inner}) ON ${q('__pivot')} USING ${using}${groupBy}${order}`;
  }

  if (def.group_bys.length > 0 && def.measures.length > 0) {
    const dimParts = def.group_bys.map(
      g => `${dimExpr(g, col)} AS ${q(dimAlias(g))}`
    );
    const measureParts = def.measures.map(
      m => `${measureExpr(m, col)} AS ${q(measureAlias(m))}`
    );
    const groupByExprs = def.group_bys.map(g => dimExpr(g, col)).join(', ');
    const aliases = [
      ...def.group_bys.map(dimAlias),
      ...def.measures.map(measureAlias),
    ];
    const inner = `SELECT ${[...dimParts, ...measureParts].join(', ')} FROM ${table}${where} GROUP BY ${groupByExprs}`;

    // DETAIL-REPORT CHART (design §1-D2). Reached only via the internal marker
    // detailChartDefinition sets, never from a persisted definition. The ordering
    // is emitted UNCONDITIONALLY here and orderClause is deliberately NOT called:
    // the derived definition already carries `sort: []`, and this is the second,
    // independent defense — a sort that leaked in (e.g. naming the chart's own
    // group_by) would otherwise hijack the top-N and select the alphabetically
    // first 50 categories while the caption still claims the largest 50.
    if (isDetailChartCompile(def)) {
      const rankBy = q(measureAlias(def.measures[0]));
      return (
        `WITH agg AS (${inner})` +
        ` SELECT agg.*, (SELECT count(*) FROM agg) AS ${q(CHART_CATEGORY_TOTAL_COLUMN)}` +
        ` FROM agg ORDER BY ${rankBy} DESC NULLS LAST LIMIT ${MAX_CATEGORIES}`
      );
    }

    const order =
      orderClause(def.sort, aliases) ||
      ` ORDER BY ${def.group_bys.map(g => `${q(dimAlias(g))} ASC`).join(', ')}`;

    // ZERO-FILLED DATE SPINE (design §1-D3, §3). Silently inert unless every
    // trigger condition holds, in which case the SQL below is byte-identical to
    // the pre-gap-fill output.
    const spine = gapFillWindow(def, dataset, related);
    if (spine) {
      const dim = q(dimAlias(def.group_bys[0]));
      // Zero-fill keys on JOIN PRESENCE, never a blanket COALESCE: a real day
      // whose matching claims aggregate to NULL (every contributing value NULL)
      // keeps its NULL — "claims with unknown amounts" is not "$0 expected" —
      // while a day absent from the aggregate gets a true 0. Legitimate for every
      // measure because trigger (c) admits only additive aggregates.
      const filled = def.measures.map(m => {
        const alias = q(measureAlias(m));
        return `CASE WHEN agg.${dim} IS NULL THEN 0 ELSE agg.${alias} END AS ${alias}`;
      });
      // `WHERE EXISTS (SELECT 1 FROM agg)` expresses "don't fabricate a window of
      // zeros over an empty result" IN SQL. compileReport is a pure synchronous
      // string builder and cannot branch on row counts, and a probe query would
      // add a round trip and a race. An empty aggregate yields zero rows, so the
      // existing "No results match your filters." state renders instead of 60
      // zero bars reading as "we expect no money".
      //
      // The outer query carries its OWN ORDER BY — a CTE wrap does not inherit
      // the inner ordering.
      //
      // BUSINESS-DAY SPINE (design §3). The generated grid is Mon-Fri only
      // (DuckDB isodow() is 1=Mon … 7=Sun), because a payment forecast that
      // lands on a weekend is simply wrong and a permanent run of $0 Sat/Sun
      // bars is ~30% of the chart. UNIONed with EVERY date the aggregate
      // actually produced — not just "weekend dates carrying dollars", which is
      // not compiler-expressible: by this point there is one aggregated measure
      // column and no way to tell an actual from a forecast. Admitting all agg
      // dates is strictly safe (an agg row is real data, so it can never
      // fabricate a synthetic zero) and makes dates(spine) ⊇ dates(agg) true BY
      // CONSTRUCTION, so the LEFT JOIN cannot drop a row and the charted total
      // always equals the aggregate's total. In practice a weekend column
      // appears only when real money genuinely landed on it.
      //
      // The CAST in the union arm pins the spine column to DATE against UNION
      // type coercion; the dim alias is read from the in-scope variable so this
      // works on any dataset's date column, dotted refs included.
      return (
        `WITH agg AS (${inner}),` +
        ` spine AS (SELECT CAST(d AS DATE) AS d FROM generate_series(DATE ${lit(spine.start)}, DATE ${lit(spine.end)}, INTERVAL 1 DAY) t(d) WHERE isodow(CAST(d AS DATE)) < 6` +
        ` UNION SELECT CAST(${dim} AS DATE) FROM agg)` +
        ` SELECT spine.d AS ${dim}, ${filled.join(', ')}` +
        ` FROM spine LEFT JOIN agg ON spine.d = agg.${dim}` +
        ` WHERE EXISTS (SELECT 1 FROM agg)${order}`
      );
    }

    return `${inner}${order}`;
  }

  // Measures with no group-by and no pivot compile to a single-row SCALAR
  // aggregate (e.g. `SELECT count(*) AS "count"`), NOT a detail dump. Reaching
  // here means assertMeasuresValid already accepted the definition, and it only
  // accepts a NON-count measure when there is a group-by or pivot — so every
  // measure here is a count(*)-style scalar aggregate. Without this branch the
  // query would fall through to the DETAIL branch below and silently return up
  // to DETAIL_ROW_LIMIT raw rows instead of the count on screen — the grain flip
  // the runtime extract-schema gate can trigger when it drops a joined report's
  // sole group-by (leaving `group_bys=[]`, `measures=[count]`). A single row
  // needs no ORDER BY beyond an explicit measure-alias sort.
  if (def.measures.length > 0 && !def.pivot_column) {
    const measureParts = def.measures.map(
      m => `${measureExpr(m, col)} AS ${q(measureAlias(m))}`
    );
    const order = orderClause(def.sort, def.measures.map(measureAlias));
    return `SELECT ${measureParts.join(', ')} FROM ${table}${where}${order}`;
  }

  // A report with grouping dimensions (a group-by or a pivot) but ZERO measures
  // cannot be answered as the aggregate the user built: every aggregate branch
  // above requires a measure, so reaching here means measures is empty. Without
  // this guard it would fall through to the DETAIL branch and silently dump raw
  // rows — the grain flip the runtime extract-schema gate (or a stale manifest)
  // triggers when it drops a report's sole measure while a group-by/pivot
  // survives, leaving `measures=[]` with grouping intact. This is the exact
  // inverse of assertMeasuresValid's "measure with no grouping" rejection;
  // surface it as an actionable invalid state instead of a detail dump that
  // answers a different question than the grouped report on screen.
  if (def.group_bys.length > 0 || def.pivot_column) {
    throw new ReportCompileError('Add a value to summarize.');
  }

  const columnNames =
    def.columns.length > 0 ? def.columns : dataset.columns.map(c => c.name);
  // A DERIVED column must be emitted as its computed expression aliased back to
  // the column name, even in non-joined mode (where materialized columns stay
  // byte-identical bare identifiers). Otherwise a detail report selecting
  // days_outstanding would emit a bare "days_outstanding" absent from the Parquet.
  const selectItems = columnNames.map(ref =>
    joined || isDerivedRef(ref, dataset, related)
      ? `${col(ref)} AS ${q(ref)}`
      : q(ref)
  );
  const order = orderClause(def.sort, columnNames);
  return `SELECT ${selectItems.join(', ')} FROM ${table}${where}${order} LIMIT ${DETAIL_ROW_LIMIT}`;
}

export function compileDistinctCount(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): string {
  if (!def.pivot_column) {
    throw new ReportCompileError(
      'compileDistinctCount requires a pivot_column'
    );
  }
  assertColumnsExist(def, dataset, related);
  const relatedKeys = joinedDatasetKeys(def);
  const col = makeColResolver(
    def,
    relatedKeys.length > 0,
    dataset,
    related,
    pacificTodayISO()
  );
  return `SELECT count(DISTINCT ${dimExpr(def.pivot_column, col)}) AS n FROM ${fromClause(def, dataset, relatedKeys)}${whereClause(def.filters, dataset, related, col)}`;
}
